import { useLocalStore } from 'mobx-react-lite';

import { IUserMention } from '@gem/models/userModel';
import { MENTION_ALL_NICKNAME, formatCounter } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { loadRangeAroundNextUserMention } from 'APP/Tasks/messaging/loadRangeAroundNextUserMention/loadRangeAroundNextUserMention';
import { UpdateReason } from 'APP/constants/scroll';
import type { Group } from 'APP/store/Groups/Group';
import Entities from 'STORE';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

export interface IScrollToMentionPresenter {
  isLoading: boolean;
  group: Group;
  badgeCount: string;
  hasScrollToMentionButton: boolean;
  moveToMention(): Promise<void>;
  findMentionMessage(): ChatMessage | null;
}

export const useScrollToMentionPresenter = (): IScrollToMentionPresenter => {
  const presenter = useLocalStore<IScrollToMentionPresenter>(() => ({
    isLoading: false,
    get group(): Group {
      return Entities.GroupsStore.activeGroup!;
    },

    get hasScrollToMentionButton(): boolean {
      return Boolean(presenter.group.counter?.unreadUserMentionsCounter);
    },

    get badgeCount(): string {
      const count = presenter.group.counter?.unreadUserMentionsCounter;
      if (count) {
        return formatCounter(count);
      }
      return '';
    },

    findMentionMessage(): ChatMessage | null {
      return (
        presenter.group.messagesStore.messages.find(
          (message: ChatMessage) =>
            message.serverTime > presenter.group.seenTs &&
            message.payload.userMentions?.find(
              (x: IUserMention) =>
                x.userId === Entities.usersStore.Me.id || x.nickname === MENTION_ALL_NICKNAME
            )
        ) || null
      );
    },

    async moveToMention(): Promise<void> {
      if (presenter.isLoading) {
        return;
      }
      presenter.isLoading = true;
      let message = presenter.findMentionMessage();
      if (!message) {
        presenter.group.messagesStore.setIsLoadingMessages(true);
        await loadRangeAroundNextUserMention(presenter.group.id, presenter.group.seenTs);
        presenter.group.messagesStore.setIsLoadingMessages(false);
        message = presenter.findMentionMessage();
      }
      if (message) {
        Tasks.messaging.focusMessage({
          groupId: presenter.group.id,
          messageId: message.id,
        });
      } else {
        Entities.ChatStore.setScrollChanged(UpdateReason.ScrollToNewMessage);
      }
      presenter.isLoading = false;
    },
  }));

  return presenter;
};
